import React, {useEffect, useState, forwardRef} from "react";
import {Table} from "antd";

// Intended to be used for AntD Tables
// It is now used for an AntD List (TestAssignments.js > AssignedTestCard.js, so it has been renamed  to with withAntdPagination
// Implement .finally(()=>{ abortController = null; }); in the passed in fetchFunction

const DEFAULT_PAGE_SIZE = 10,
    DEFAULT_CURRENT_PAGE = 1;
const withAntDPagination = (WrappedComponent, fetchFunction, params, onReady) => {
    return forwardRef((props, ref) => {
        let {dynamicWidth = false, scroll } = props;

        let {pageSize, currentPage} = params;
        if(!pageSize){
            pageSize = DEFAULT_PAGE_SIZE;
        }
        if(!currentPage){
            currentPage = DEFAULT_CURRENT_PAGE;
        }
        const [dataSource, setDataSource] = useState([]),
            [pageData, setPageData] = useState({currentPage:currentPage, totalResults:0, pageSize:pageSize}),
            [loading, setLoading] = useState(false),
            [showOnePageTotal, setShowOnePageTotal] = useState(false);

        const handleSetData = (data)=>{
            setDataSource(data);
            setLoading(false);
        }
        const handlePageData = (updatePageData)=>{
            const {pageSize, totalResults} = updatePageData;
            setPageData({...pageData, totalResults, pageSize});
            setShowOnePageTotal(totalResults && totalResults <= pageSize);
        }
        const observePaginationAriaLabels = () => {
            const paginationContainer = document.querySelector(".ant-pagination");
            if (!paginationContainer) {
                return () => {};
            }

            const setLabels = () => {
                const prevButton = document.querySelector(".ant-pagination-prev button");
                const nextButton = document.querySelector(".ant-pagination-next button");
                const currentPageButton = document.querySelector('.ant-pagination-item-active button');

                if (prevButton) {
                    prevButton.setAttribute("aria-label", "Previous page");
                }
                if (nextButton) {
                    nextButton.setAttribute("aria-label", "Next page");
                }
                if(currentPageButton){
                    currentPageButton.setAttribute('aria-current','page');
                }
            };

            setLabels();

            const observer = new MutationObserver(() => {
                setLabels();
            });

            observer.observe(paginationContainer, {
                childList: true,
                subtree: true,
            });

            return () => observer.disconnect();
        }

        useEffect(() => {
            if (
                pageData &&
                pageData.currentPage > 1 &&
                pageData.currentPage > Math.ceil(pageData.totalResults / pageData.pageSize)
            ) {
                setPageData({ ...pageData, currentPage: 1 });
            }

            const disconnectObserver = observePaginationAriaLabels();
            return()=>{
                disconnectObserver();
            }
        }, [pageData]);

        useEffect(() => {
            setLoading(true);
            const abortController = new AbortController();
            getData(abortController);

            return()=>{
                if(abortController){
                    abortController.abort();
                }
            }
        }, [pageData.currentPage, pageData.pageSize]);

        useEffect(()=>{
            setLoading(true);
            const abortController = new AbortController();
            if(props.reload != null){
                getData(abortController);
            }
            return()=>{
                if(abortController){
                    abortController.abort();
                }
            }
        },[props.reload])

        useEffect(() => {
            if (dataSource) {
                if (onReady && typeof onReady === "function") {
                    requestAnimationFrame(() => {
                        onReady();
                    });
                }
            }
        }, [dataSource]);


        const handlePageChange = (currentPage, pageSize) => {
            setPageData({...pageData, pageSize, currentPage});
        };

        const getData = (abortController)=>{
            if(pageData.currentPage && !isNaN(pageData.currentPage)){
                const fetchParams = {pageNumber:pageData.currentPage, pageSize:pageData.pageSize, ...params};
                fetchFunction(fetchParams, handleSetData, handlePageData, abortController);
            }else{
                setLoading(false);
            }
        }

        const hasRows = dataSource && dataSource.length > 0;
        const effectiveScroll = hasRows ? scroll : undefined;

        return (
            <div ref={ref}>
                <WrappedComponent
                    dataSource={dataSource}
                    {...props}
                    scroll={effectiveScroll}
                    loading={(loading || props.loading)}
                    size={'small'}
                    role="grid"
                    pagination={{
                        showSizeChanger: false,
                        current: pageData.currentPage,
                        total: pageData.totalResults,
                        pageSize: pageData.pageSize,
                        hideOnSinglePage: !pageData || pageData.totalResults == 0 ? true : false,
                        position: props.position ? props.position : ((WrappedComponent === Table) ? ['topRight', 'bottomRight'] : 'both'),
                        onChange: handlePageChange,
                        showTotal: (total, range) => (showOnePageTotal ? `` : `${range[0]}-${range[1]} of `) + `${total} item` + (pageData.totalResults > 1 ? `s` : ``),
                        itemRender: (_, type, originalElement) => {
                            if (showOnePageTotal) return null;
                            if(type === 'page' && originalElement.type === 'a') {
                                return <button tabIndex={-1} className="link-button">{_}</button>
                            }
                            return originalElement;
                        }
                    }}
                />
            </div>
        );
    });
}

export default withAntDPagination;